import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { supabase } from '../utils/supabaseClient';
import { Send, User, Search, Zap, ExternalLink, ShieldCheck, MessageSquare } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useLanguage } from '../context/LanguageContext';

const SharedPostCard = ({ content, isSent }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Regex to parse the structured message
  const transmissionMatch = content.match(/\[SHARED TRANSMISSION\](?:\n\nNote from sender: "([\s\S]*?)")?\n\n([\s\S]*?)\n\nView full post: (.*)/);

  if (!transmissionMatch) return <div style={{ wordBreak: 'break-word' }}>{content}</div>;

  const [_, note, postPreview, fullUrl] = transmissionMatch;

  const handleAccess = () => {
    try {
      const url = new URL(fullUrl);
      navigate(url.pathname + url.search);
    } catch (e) {
      window.location.href = fullUrl;
    }
  };

  return (
    <div style={{
      width: '100%',
      minWidth: '260px',
      background: 'rgba(0,0,0,0.2)',
      borderRadius: '16px',
      overflow: 'hidden',
      border: '1px solid var(--glass-border)',
      marginTop: '0.5rem'
    }}>
      {/* Card Header */}
      <div style={{
        padding: '0.6rem 1rem',
        background: isSent ? 'rgba(255,255,255,0.1)' : 'rgba(99, 102, 241, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        borderBottom: '1px solid var(--glass-border)'
      }}>
        <ShieldCheck size={14} color={isSent ? 'white' : 'var(--accent-primary)'} />
        <span style={{
          fontSize: '0.65rem',
          fontWeight: 900,
          letterSpacing: '0.1em',
          color: isSent ? 'white' : 'var(--accent-primary)'
        }}>
          {t('secured_transmission')}
        </span>
      </div>

      {/* Note Section (If exists) */}
      {note && (
        <div style={{
          padding: '1rem',
          background: 'rgba(255,255,255,0.03)',
          borderBottom: '1px solid var(--glass-border)',
          fontStyle: 'italic',
          fontSize: '0.85rem'
        }}>
          <MessageSquare size={12} style={{ marginBottom: '0.3rem', opacity: 0.5 }} />
          <div style={{ color: isSent ? 'rgba(255,255,255,0.9)' : 'var(--text-body)' }}>
            "{note}"
          </div>
        </div>
      )}

      {/* Post Preview */}
      <div style={{ padding: '1.25rem' }}>
        <p style={{
          fontSize: '0.8rem',
          lineHeight: '1.4',
          color: isSent ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {postPreview}
        </p>
      </div>

      {/* Action Button */}
      <button
        onClick={handleAccess}
        style={{
          width: '100%',
          padding: '0.8rem',
          background: isSent ? 'rgba(255,255,255,0.15)' : 'var(--bg-deep)',
          border: 'none',
          borderTop: '1px solid var(--glass-border)',
          color: isSent ? 'white' : 'var(--accent-primary)',
          fontSize: '0.75rem',
          fontWeight: 900,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s ease'
        }}
        className="hover-bright"
      >
        <Zap size={14} />
        {t('access_full_feed')}
      </button>
    </div>
  );
};

function Messages() {
  const { t } = useLanguage();
  const { refreshCounts } = useNotifications();
  const [connections, setConnections] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showMobileList, setShowMobileList] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem('user'));

  const location = useLocation();
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    if (selectedUser && messages.length > 0) {
      setConnections(prev => {
        const others = prev.filter(c => Number(c.id) !== Number(selectedUser.id));
        return [selectedUser, ...others];
      });
    }
  }, [messages.length, selectedUser?.id]);

  const fetchConversations = async () => {
    try {
      const { data } = await api.get('/messages/conversations');
      setConnections(data);
    } catch (err) {
      console.error('Failed to fetch conversations', err);
    }
  };

  // 1. Initial Fetch on Mount
  useEffect(() => {
    fetchConversations();
    const inv = setInterval(fetchConversations, 30000);
    return () => clearInterval(inv);
  }, []);

  // 2. Handle Navigation / Query Params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userIdFromQuery = params.get('user');
    
    if (userIdFromQuery) {
      const targetId = Number(userIdFromQuery);
      
      const processTarget = async () => {
        try {
          // Check if already in current connections state
          // (We use a functional update later to be sure we have the latest)
          let target;
          
          const { data: userData } = await api.get(`/users/${targetId}`);
          target = userData;
          
          setConnections(prev => {
            if (prev.find(u => Number(u.id) === targetId)) return prev;
            return [target, ...prev];
          });
          
          setSelectedUser(target);
          setShowMobileList(false);
          setTimeout(() => inputRef.current?.focus(), 500);
          
          // Clear query param IMMEDIATELY to prevent loops
          navigate('/messages', { replace: true });
        } catch (e) {
          console.error("Failed to process message target from URL", e);
        }
      };
      
      processTarget();
    }
  }, [location.search]); // Only re-run when the URL search changes

  useEffect(() => {
    if (!selectedUser) return;

    const fetchMsgs = async () => {
      try {
        const { data } = await api.get(`/messages/${selectedUser.id}`);
        setMessages(data);
        refreshCounts();
      } catch (err) {
        console.error('Failed to fetch messages');
      }
    };

    fetchMsgs();

    // REAL-TIME SUBSCRIPTION
    const channel = supabase
      .channel(`chat:${selectedUser.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          // Filter for messages involving the current user and the selected user
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const newMessage = payload.new;
            // Only add if it belongs to this conversation
            const isRelevant = 
              (Number(newMessage.sender_id) === Number(selectedUser.id) && Number(newMessage.receiver_id) === Number(currentUser.id)) ||
              (Number(newMessage.sender_id) === Number(currentUser.id) && Number(newMessage.receiver_id) === Number(selectedUser.id));

            if (isRelevant) {
              setMessages(prev => {
                // Prevent duplicates from local optimistic updates
                if (prev.find(m => m.id === newMessage.id)) return prev;
                return [...prev, newMessage];
              });
              
              // If we just received a message, mark as read on backend
              if (Number(newMessage.sender_id) === Number(selectedUser.id)) {
                api.get(`/messages/${selectedUser.id}`).then(({ data }) => setMessages(data));
                refreshCounts();
              }
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedMsg = payload.new;
            setMessages(prev => prev.map(m => m.id === updatedMsg.id ? updatedMsg : m));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedUser?.id]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;
    try {
      await api.post('/messages', { receiver_id: selectedUser.id, content: newMessage });
      const sentMsg = {
        id: Date.now(),
        sender_id: currentUser.id,
        receiver_id: selectedUser.id,
        content: newMessage,
        created_at: new Date().toISOString(),
        is_read: 0
      };
      setMessages(prev => [...prev, sentMsg]);
      setNewMessage('');

      setConnections(prev => {
        const others = prev.filter(c => Number(c.id) !== Number(selectedUser.id));
        return [selectedUser, ...others];
      });

      const { data } = await api.get(`/messages/${selectedUser.id}`);
      setMessages(data);
      refreshCounts();
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const filteredConnections = connections.filter(conn =>
    (conn.full_name || conn.username || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="glass-card animate-fade" style={{
      display: 'flex',
      height: 'calc(100vh - 200px)',
      padding: 0,
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Sidebar */}
      <div className={`w-full-mobile ${!showMobileList ? 'hide-mobile' : ''}`} style={{
        width: '320px',
        borderRight: '1px solid var(--glass-border)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-surface)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--glass-border)' }}>
          <h2 style={{ fontSize: '1.4rem', color: 'var(--text-heading)', letterSpacing: '-0.02em' }}>
            AP <span style={{ color: 'var(--accent-primary)' }}>{t('messages_title')}</span>
          </h2>
          <div style={{ marginTop: '1rem', position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder={t('search_messages')}
              className="cyber-input"
              style={{ padding: '0.6rem 1rem 0.6rem 2.5rem', fontSize: '0.85rem' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
          {filteredConnections.length > 0 ? (
            filteredConnections.map(conn => (
              <div
                key={conn.id}
                onClick={() => { setSelectedUser(conn); setShowMobileList(false); }}
                style={{
                  padding: '1.1rem',
                  margin: '0.4rem 0.5rem',
                  cursor: 'pointer',
                  borderRadius: '16px',
                  background: Number(selectedUser?.id) === Number(conn.id) ? 'rgba(99, 102, 241, 0.12)' : 'transparent',
                  border: '1px solid',
                  borderColor: Number(selectedUser?.id) === Number(conn.id) ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <div style={{
                  width: '44px', height: '44px', borderRadius: '50%',
                  background: 'var(--bg-surface)', border: '1px solid var(--glass-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
                }}>
                  {conn.avatar_url ? (
                    <img src={conn.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <User size={20} color={Number(selectedUser?.id) === Number(conn.id) ? 'var(--accent-primary)' : 'var(--text-muted)'} />
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 800, color: 'var(--text-heading)', fontSize: '0.95rem' }}>{conn.full_name || conn.username}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 700, letterSpacing: '0.05em' }}>
                    {Number(selectedUser?.id) === Number(conn.id) ? t('active_status') : t('ready_status')}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>{t('no_messages') || t('no_messages_found')}</div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={showMobileList ? 'hide-mobile' : ''} style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-apex)'
      }}>
        {selectedUser ? (
          <>
            <div style={{
              padding: '1.25rem 2rem', background: 'var(--bg-card)',
              borderBottom: '1px solid var(--glass-border)', display: 'flex',
              alignItems: 'center', gap: '1.25rem', zIndex: 10
            }}>
              <button className="mobile-only" onClick={() => setShowMobileList(true)} style={{ background: 'transparent', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer', outline: 'none', fontSize: '1.2rem', padding: '0 0.5rem' }}>
                ←
              </button>
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: 'var(--accent-success)', boxShadow: '0 0 12px var(--accent-success)' }}></div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800 }}>{selectedUser.full_name || selectedUser.username}</h3>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {messages.map(msg => {
                const isSent = Number(msg.sender_id) !== Number(selectedUser.id);
                return (
                  <div key={msg.id} style={{
                    alignSelf: isSent ? 'flex-end' : 'flex-start',
                    maxWidth: '75%', display: 'flex', flexDirection: 'column',
                    alignItems: isSent ? 'flex-end' : 'flex-start'
                  }}>
                    <div style={{
                      background: isSent ? 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))' : 'var(--bg-card)',
                      color: isSent ? 'var(--btn-text)' : 'var(--text-heading)',
                      padding: '1rem 1.5rem', borderRadius: isSent ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                      border: '1px solid var(--glass-border)', fontSize: '1rem', lineHeight: '1.5'
                    }}>
                      {msg.content.includes('[SHARED TRANSMISSION]') ? (
                        <SharedPostCard content={msg.content} isSent={isSent} />
                      ) : (
                        msg.content
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.2rem' }}>
                      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                      </span>
                      {isSent && msg.is_read === 1 && (
                        <span style={{ fontSize: '0.65rem', color: 'var(--accent-success)', fontWeight: 800, letterSpacing: '0.05em' }}>
                          ✓ {t('seen_status')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSend} style={{ padding: '1.5rem 2rem', background: 'var(--bg-card)', borderTop: '1px solid var(--glass-border)', display: 'flex', gap: '1rem' }}>
              <input
                ref={inputRef}
                className="cyber-input"
                placeholder={t('comment_placeholder')}
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                style={{ flex: 1 }}
              />
              <button type="submit" className="btn-cyber" style={{ padding: '0 1.5rem', width: 'auto' }}>
                <Send size={18} />
              </button>
            </form>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
            <Send size={48} style={{ opacity: 0.2, marginBottom: '1rem' }} />
            <p style={{ letterSpacing: '0.1em' }}>{t('select_conversation')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Messages;
